import * as React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { ConnectionType, IConnectionAttendeeDTO } from '../../../../../../api/connections.dao';
import { CommonModal } from '../../../../../global/modal/common/common-modal';
import { DefaultCloseModalButton } from '../../../../../global/modal/common/common-close-modal-buttons';
import { FormField } from '../../../../../global/form/form-field/form-field';
import DefaultButton from '../../../../../global/default-button/default-button';
import './create-panelist-link.scss';
import { CreatePanelistLinkVM } from './create-panelist-link.viewmodel';
import {
    ADD_DETAILS_MANUALLY_TEXT,
    CREATE_PANELIST_LINK_TEXT,
    HOSTS,
    PRESENTERS,
    SUPPORT,
    WEBINAR_ID,
} from '../../../../../../constants/panelist-link-connection.constants';
import classNames from 'classnames';
import { OptionIcon, OptionIconChecked } from '../../../../../global/oe-radio-options/option-icon';
import Text from '../../../../../global/text/text';
import { CreatePanelistLinkFormDataVM } from './create-panelist-link-form-data.viewmodel';

export interface IPresentationProps {
    vm: CreatePanelistLinkVM;
}

const CreatePanelistLinkModal = mobxReact.observer(
    (props: IPresentationProps) => {
        const { vm } = props;
        const { intl } = vm;

        const formVM = vm;
        const formData = formVM.createPanelistLinkFormDataVM;

        const handleAttendeeChange = (attendee: IConnectionAttendeeDTO) => {
            formData.setAttendeeID(attendee.attendeeId);
            formData.setAttendeeName(`${attendee.firstName} ${attendee.lastName}`);
            formData.setAttendeeEmail(attendee.email);
        };

        const footer = (
            <div className="buttons-block">
                <DefaultButton
                    text={ADD_DETAILS_MANUALLY_TEXT}
                    modifier={['fat', 'cancel']}
                    disabled={formVM.getIsFormSaving() || !formData.getAttendeeID()}
                    onClick={vm.openPanelistLinkConnectionForm.bind(vm, {
                        type: ConnectionType.PanelistLink,
                        connectionId: formData.getWebinarID(),
                        label: `${formData.getAttendeeName()}'s Link`,
                        attendeeId: formData.getAttendeeID(),
                        connectionVersion: formData.getConnectionVersion(),
                        parentJoinMethodId: formData.getParentJoinMethodID(),
                        parentMeetingConnectionId: formData.getParentMeetingConnectionID(),
                    })}
                />
                <div className="buttons-block buttons-block--from-right">
                    <DefaultButton
                        text={CREATE_PANELIST_LINK_TEXT}
                        modifier={['fat', 'confirm-safe']}
                        onClick={formVM.createPanelistLinkHandle}
                        disabled={formVM.getIsFormSaving() || !formData.getAttendeeID()}
                    />
                    <DefaultCloseModalButton />
                </div>
            </div>
        );

        return (
            <>
                <CommonModal
                    title={CREATE_PANELIST_LINK_TEXT}
                    isOpen={formVM.getIsFormShown()}
                    onRequestClose={() => {
                        formVM.hideForm();
                    }}
                    className="oe-modal-width create-panelist-link-modal"
                    classNameBody="body__scrollable"
                    footer={footer}
                >
                    <form className="oe-form">
                        <FormField
                            className="m-bottom-20px"
                            label={intl.formatMessage(WEBINAR_ID)}
                        >
                            <select
                                className="oe-form-field__control"
                                value={formData.getWebinarID()}
                                onChange={item => formData.handleWebinarChange(item.target.value)}
                            >
                                {formData.getWebinarDetails().map(item => {
                                    return (
                                        <option
                                            key={item.connectionId}
                                            value={item.connectionId}
                                        >
                                            {item.connectionId}
                                        </option>
                                    );
                                })}
                            </select>
                        </FormField>

                        <FormField
                            className="m-bottom-20px"
                            label={intl.formatMessage(PRESENTERS)}
                        >
                            <div className='attendee-info-section'>
                                {formData.getPresenters()?.map(presenter => (
                                   <AttendeeList
                                        key={presenter.organizationId}
                                        attendees={presenter.attendees}
                                        name={presenter.name}
                                        onAttendeeChange={handleAttendeeChange}
                                        formData={formData}
                                    />
                                ))}
                                {formData.getPresenters()?.length === 0 &&
                                    <div className="no-attendee-info">
                                        <FormattedMessage id="oe.panelistLink.noneAdded" defaultMessage="None Added" />
                                    </div>
                                }
                            </div>
                        </FormField>

                        <FormField
                            className="m-bottom-20px"
                            label={intl.formatMessage(HOSTS)}
                        >
                            <div className='attendee-info-section'>
                                {formData.getHosts()?.map(host => (
                                    <AttendeeList
                                        key={host.organizationId}
                                        attendees={host.attendees}
                                        name={host.name}
                                        onAttendeeChange={handleAttendeeChange}
                                        formData={formData}
                                    />
                                ))}
                                {formData.getHosts()?.length === 0 &&
                                    <div className="no-attendee-info">
                                        <FormattedMessage id="oe.panelistLink.noneAdded" defaultMessage="None Added" />
                                    </div>
                                }
                            </div>
                        </FormField>
                        <FormField
                            className="m-bottom-20px"
                            label={intl.formatMessage(SUPPORT)}
                        >
                            <div className='attendee-info-section'>
                                {formData.getSupport()?.map(support => (
                                    <AttendeeList
                                        key={support.organizationId}
                                        attendees={support.attendees}
                                        name={support.name}
                                        onAttendeeChange={handleAttendeeChange}
                                        formData={formData}
                                    />
                                ))}
                                {formData.getSupport()?.length === 0 &&
                                    <div className="no-attendee-info">
                                        <FormattedMessage id="oe.panelistLink.noneAdded" defaultMessage="None Added" />
                                    </div>
                                }
                            </div>
                        </FormField>
                    </form>
                </CommonModal>
            </>
        );
    }
);

const AttendeeList: React.FC<{
    attendees: IConnectionAttendeeDTO[];
    name: string;
    onAttendeeChange: (attendee: IConnectionAttendeeDTO) => void;
    formData: CreatePanelistLinkFormDataVM;
}> = ({ attendees, name, onAttendeeChange, formData }) => (
    <div>
        <div className="organization__header">{name}</div>
        {attendees?.length > 0 && attendees.map(attendee => (
                <div className="attendee" key={attendee.attendeeId}>
                    <label htmlFor={`radio-button-${attendee.attendeeId}`}
                        className={classNames('oe-radio-option__label', {
                            'oe-radio-option--selected': attendee.attendeeId === formData.getAttendeeID()
                        })}
                        onClick={e => {
                            e.stopPropagation();
                            onAttendeeChange(attendee);
                        }}>
                        <Text
                            className='oe-radio-option__label-text'
                            id={attendee.attendeeId?.toString()}
                            message={`${attendee.firstName} ${attendee.lastName}`}
                            withEllipsis
                        />
                        {attendee.attendeeId === formData.getAttendeeID() ? <OptionIconChecked /> : <OptionIcon />}
                    </label>
                </div>
            ))}
    </div>
);

export { CreatePanelistLinkModal };
